import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';

const BlockDayModal = ({ isOpen, onClose, date, scheduleJobs, onBlock, language = 'pt-BR' }) => {
  const [selectedTeam, setSelectedTeam] = useState('');
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);

  // Calcular jobs por PM naquele dia
  const dateStr = date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` : '';
  
  const dayJobs = scheduleJobs.filter(j => j.date === dateStr);
  
  // Agrupar por PM
  const pmGroups = {};
  dayJobs.forEach(job => {
    const pm = job.team;
    pmGroups[pm] = (pmGroups[pm] || 0) + 1;
  });
  
  // Lista de PMs (PM1 a PM10) - sempre mostrar todos
  const pmsToShow = ['PM1', 'PM2', 'PM3', 'PM4', 'PM5', 'PM6', 'PM7', 'PM8', 'PM9', 'PM10'];
  
  // Verificar se pode bloquear ALL
  const canBlockAll = true;
  
  const handleBlock = async () => {
    if (!selectedTeam) return;
    
    setLoading(true);
    await onBlock(selectedTeam, reason);
    setLoading(false);
    
    // Reset
    setSelectedTeam('');
    setReason('');
  };

  useEffect(() => {
    if (!isOpen) {
      setSelectedTeam('');
      setReason('');
    }
  }, [isOpen]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span className="text-2xl">🔒</span>
            {language === 'pt-BR' ? 'Bloquear Dia' : 'Block Day'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Data */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-sm font-semibold text-blue-900">
              📅 {date?.toLocaleDateString('pt-BR', { day: 'numeric', month: 'long', year: 'numeric' })}
            </p>
          </div>

          {/* Seleção de PM */}
          <div>
            <Label className="text-sm font-semibold mb-2 block">
              {language === 'pt-BR' ? 'Selecione qual PM bloquear:' : 'Select which PM to block:'}
            </Label>
            
            <RadioGroup value={selectedTeam} onValueChange={setSelectedTeam}>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {pmsToShow.map(pm => {
                  const jobCount = pmGroups[pm] || 0;
                  const hasJobs = jobCount > 0;
                  
                  return (
                    <div
                      key={pm}
                      className={`flex items-center space-x-2 p-2 rounded border ${
                        hasJobs ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200 hover:bg-green-100'
                      }`}
                    >
                      <RadioGroupItem value={pm} id={pm} disabled={hasJobs} />
                      <label
                        htmlFor={pm}
                        className={`flex-1 text-sm cursor-pointer ${hasJobs ? 'text-gray-400' : 'text-gray-900'}`}
                      >
                        <span className="font-semibold">{pm}</span>
                        {hasJobs ? (
                          <span className="text-red-600 ml-2">
                            ({jobCount} {language === 'pt-BR' ? 'trabalho(s)' : 'job(s)'}) ❌
                          </span>
                        ) : (
                          <span className="text-green-600 ml-2">
                            ({language === 'pt-BR' ? 'Livre' : 'Free'}) ✅
                          </span>
                        )}
                      </label>
                    </div>
                  );
                })}
                
                {/* Separador */}
                <div className="border-t border-gray-300 my-2"></div>
                
                {/* Opção TODOS */}
                <div
                  className={`flex items-center space-x-2 p-2 rounded border ${
                    !canBlockAll ? 'bg-red-50 border-red-200' : 'bg-purple-50 border-purple-200 hover:bg-purple-100'
                  }`}
                >
                  <RadioGroupItem value="ALL" id="ALL" disabled={!canBlockAll} />
                  <label
                    htmlFor="ALL"
                    className={`flex-1 text-sm font-semibold cursor-pointer ${!canBlockAll ? 'text-gray-400' : 'text-purple-900'}`}
                  >
                    {language === 'pt-BR' ? 'TODOS OS PMs' : 'ALL PMs'}
                    {!canBlockAll && (
                      <div className="text-xs text-red-600 mt-1">
                        ⚠️ {Object.keys(pmGroups).join(', ')} {language === 'pt-BR' ? 'têm trabalhos' : 'have jobs'}
                      </div>
                    )}
                  </label>
                </div>
              </div>
            </RadioGroup>
          </div>

          {/* Motivo */}
          <div>
            <Label htmlFor="reason" className="text-sm">
              {language === 'pt-BR' ? 'Motivo (opcional):' : 'Reason (optional):'}
            </Label>
            <Input
              id="reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={language === 'pt-BR' ? 'Ex: Feriado, Férias, etc' : 'Ex: Holiday, Vacation, etc'}
              className="mt-1"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={loading}>
            {language === 'pt-BR' ? 'Cancelar' : 'Cancel'}
          </Button>
          <Button
            onClick={handleBlock}
            disabled={!selectedTeam || loading}
            className="bg-red-600 hover:bg-red-700"
          >
            {loading ? (language === 'pt-BR' ? 'Bloqueando...' : 'Blocking...') : 
                       (language === 'pt-BR' ? '🔒 Bloquear' : '🔒 Block')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BlockDayModal;
